import _ from 'lodash'

import { FramesManager } from '../../../../view/frames-manager'

import { UI2EditableFieldConfigProps } from './ui2-editable-field.config'

export interface UI2EditableFieldInternalProps<T = any> {
  /**
   * Commit a new value. The trailing params are injected per field by EntityRenderer: silentUpdate
   * routes through value.commit() (doesn't dirty the form); overridePath writes to a different path.
   */
  onChange?: (value: T, silentUpdate?: boolean, overridePath?: string) => void
  /** Renderer errors for this value path; consumed by the shell and by composite fields. */
  errors?: any[]
  /** Domain copy for the prefilled Edit/Cancel buttons (e.g. 'Add/remove photos'). */
  prefilledEditText?: string
  prefilledCancelText?: string
  /**
   * For a helper field handed the parent's frames, which would otherwise claim the parent's
   * focus-registry key. See ui2-field-shell.ts (suppressFocusTargetRegistration).
   */
  suppressFocusTargetRegistration?: boolean
}

export const IS_REQUIRED_UI_ERROR = 'is required'

export const IS_REJECTED_UI_ERROR = 'value was rejected'

/**
 * Minimal reviewField value the shells read for rejection state. ReviewValue (ui2-review-field.config)
 * satisfies this and adds reviewedBy.
 */
export interface IRejectionValue {
  status?: 'accepted' | 'rejected' | 'overridden'
  reason?: string
  originalValue?: any
}

interface IFilterUiErrorsOptions {
  isRequired?: boolean
  isReadOnly?: boolean
  isDisabled?: boolean
}

export function filterUiErrors(
  errors: any[] | undefined,
  options: IFilterUiErrorsOptions,
): any[] | undefined {
  if (!errors || errors.length === 0) return errors
  const { isRequired, isReadOnly, isDisabled } = options
  if (isRequired && !isReadOnly && !isDisabled) return errors
  const isRequiredMessage = (candidate: any): boolean => {
    if (typeof candidate === 'string') return candidate === IS_REQUIRED_UI_ERROR
    if (Array.isArray(candidate)) return candidate.every(isRequiredMessage)
    const message = candidate?.detail || candidate?.message
    return message === IS_REQUIRED_UI_ERROR
  }
  return errors.filter((entry) => !isRequiredMessage(entry))
}

/** Shared emptiness predicate — three consumers (mergeUIStateErrors, per-field reporter, UI2FieldShell) must agree on which fields count as unfilled. */
export interface IIsRequiredEmptyArgs {
  isRequired?: boolean
  isReadOnly?: boolean
  isDisabled?: boolean
  value: unknown
  isValueEmpty?: (value: unknown) => boolean
}

export function isRequiredEmpty(args: IIsRequiredEmptyArgs): boolean {
  const { isRequired, isReadOnly, isDisabled, value, isValueEmpty } = args
  if (!isRequired) return false
  if (isReadOnly || isDisabled) return false
  return isValueEmpty ? isValueEmpty(value) : isValueDefaultEmpty(value)
}

function isValueDefaultEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

interface FieldPathSource {
  frames?: FramesManager
  /** Present on components bound to a transient variable rather than an entity path. */
  transientValue?: string
}

/**
 * The key a bound ui2 component registers with FormFocusTargetContext. MUST match
 * mergeUIStateErrors (entity/renderer.tsx): a transient-bound component's error key is the
 * transient name, not the valuePath, so a valuePath registration would never resolve in
 * focusFirstError. Shared so shell-less components (ui2:geolocationField) reach the same key.
 */
export function resolveBoundFieldPath(props: FieldPathSource): string | undefined {
  const transientValue = (props as any).transientValue
  if (typeof transientValue === 'string' && transientValue.length > 0) {
    return transientValue
  }
  const valuePath: any = props.frames?.getContext?.('valuePath')
  if (!_.isArray(valuePath) || _.isEmpty(valuePath)) return undefined
  return valuePath.join('.')
}

export interface InheritableBoundFieldState {
  isDisabled: boolean;
  isReadOnly: boolean;
}

export interface PrefilledFieldState<T> {
  /** Value on entering prefilled mode; restored if editing is cancelled. */
  originalValue: T | undefined;
  isEditing: boolean;
}

export function resolveInheritedFieldState(
  props: UI2EditableFieldConfigProps,
  inheritedState?: Partial<InheritableBoundFieldState>
): InheritableBoundFieldState {
  return {
    isDisabled:
      inheritedState?.isDisabled === true ? true
        : props.isDisabled ?? false,

    isReadOnly:
      inheritedState?.isReadOnly === true ? true
        : props.isReadOnly ?? false,
  };
}

export function hasFieldValue(value: unknown): boolean {
  return value !== undefined &&
    value !== null &&
    value !== "" &&
    !(Array.isArray(value) && value.length === 0);
}

export function createPrefilledFieldState<T>(
  value: T | undefined,
  enablePrefilledMode: boolean
): PrefilledFieldState<T> {
  const shouldStartInDisplayMode =
    enablePrefilledMode && hasFieldValue(value);

  return {
    originalValue: shouldStartInDisplayMode ? value : undefined,
    isEditing: !shouldStartInDisplayMode,
  };
}

export function beginPrefilledEdit<T>(
  state: PrefilledFieldState<T>
): PrefilledFieldState<T> {
  return { ...state, isEditing: true };
}

export function cancelPrefilledEdit<T>(
  state: PrefilledFieldState<T>
): PrefilledFieldState<T> {
  return { ...state, isEditing: false };
}
